import dayjs from 'dayjs';
import postal from 'postal';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { RxDatabase } from 'rxdb';
import { Subscription } from 'rxjs';

import { useMst } from '../mobx-models/Root';
import { saveSyncTimeToLocalStorage } from '../utilities/syncHelper';
import { useFeatureFlags } from '../utilities/useFeatureFlag';
import { useOnlineStatus } from '../utilities/useOnlineStatus';
import type { FeatureFlags } from '../types/featureFlags';
import RxdbManager from './RxdbManager';

type IuseRxDB = {
  db: RxdbManager | RxDatabase;
};

const RxDBContext = createContext<null | IuseRxDB>(null);
export const RxDBProvider = RxDBContext.Provider;
const DEBUG_RXDB = localStorage.getItem('DEBUG_RXDB') === 'true';

new RxdbManager(DEBUG_RXDB);

export const RxDBHandler = ({ children }: { children: React.ReactNode }) => {
  const isOnline = useOnlineStatus();
  const { SiteDiscipline, HandleGesture } = useMst();
  const RxdbManagerRef = useRef(RxdbManager.instance);
  const featureFlag: FeatureFlags = useFeatureFlags();

  const [siteDisciplineUpdated, setSiteDisciplineUpdated] = useState<number>(
    SiteDiscipline.disciplineId > 0 && SiteDiscipline.siteId > 0 ? dayjs().unix() : 0,
  );
  const [aimsDataSync, setAimsDataSync] = useState({ message: '', allowSync: true });
  const [isRxDBInitialized, setIsRxDBInitialized] = useState(false);

  useEffect(() => {
    if (featureFlag && featureFlag?.aimsDataSyncIsActive) {
      const { aimsDataSyncIsActive, aimsDataSyncMessage } = featureFlag;
      setAimsDataSync({
        message: String(aimsDataSyncMessage ?? ''),
        allowSync: aimsDataSyncIsActive as boolean,
      });
    }
  }, [featureFlag]);

  useEffect(() => {
    if (isRxDBInitialized) {
      if (aimsDataSync?.allowSync && isOnline && siteDisciplineUpdated) {
        RxdbManagerRef.current.startReplication({
          isShowSync: SiteDiscipline.siteDisciplineUpdateInitialSync,
        });
      } else {
        RxdbManagerRef.current.stopReplication();
      }
    }
  }, [isOnline, isRxDBInitialized, aimsDataSync?.allowSync, siteDisciplineUpdated]);

  useEffect(() => {
    const postalSub = postal.subscribe({
      channel: 'db',
      topic: 'db.initialized',
      callback: () => {
        setIsRxDBInitialized(true);
      },
    });

    return () => {
      postalSub?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const postalSub = postal.subscribe({
      channel: 'site.discipline',
      topic: 'site.discipline.updated',
      callback: () => {
        setSiteDisciplineUpdated(dayjs().unix());
      },
    });
    return () => {
      postalSub?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const subArray: Subscription[] = [];
    const postalSub = postal.subscribe({
      channel: 'sync',
      topic: 'sync.online',
      callback: () => {
        RxdbManagerRef.current?.syncStates?.forEach(state => {
          const sub = state.received$.subscribe({
            error: () => {
              HandleGesture.setErrorInManualSync(true);
            },
            complete: () => {
              saveSyncTimeToLocalStorage();
            },
          });
          subArray.push(sub);
        });
      },
    });

    return () => {
      postalSub.unsubscribe();
      subArray.forEach(sub => sub.unsubscribe());
    };
  }, []);

  // Check every 60 seconds if all collections are syncing, and gracefully recover if not
  const checkForSyncRunning = useCallback(async () => {
    const nonRunningCollections =
      RxdbManagerRef.current?.syncStates?.filter(state => state.isStopped()) || [];
    return nonRunningCollections;
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      const nonRunningCollections = await checkForSyncRunning();
      if (nonRunningCollections.length > 0 && isOnline && aimsDataSync?.allowSync) {
        RxdbManagerRef.current.startReplication();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [checkForSyncRunning, isRxDBInitialized, isOnline, aimsDataSync?.allowSync]);

  return <RxDBProvider value={{ db: RxdbManagerRef.current }}>{children}</RxDBProvider>;
};

// Hook for useRxDB
export const useRxDB = () => {
  const rxDB = useContext(RxDBContext);
  if (rxDB === null) {
    throw new Error('RxDB cannot be null, please add a context provider');
  }

  const { db } = rxDB;

  return { db };
};

export default useRxDB;
